import React from 'react';
import { reportSilentError } from '../utils/silentErrorReporter';

/**
 * 🟥 CUADRO NEGRO 2026-07-03 — "MOSTRAR SIEMPRE": el Área de Trabajo NUNCA
 * muestra ventanas de error. Si el árbol React crashea, renderizamos un
 * contenedor VACÍO en su lugar. El error se guarda en window.__damyxia_errors
 * para el panel avanzado y se emite via CustomEvent para debugging.
 *
 * Se preservan:
 *   - componentDidCatch → console.error para debugging en devtools.
 *   - `handleReset` accesible programáticamente vía ref o CustomEvent.
 *   - data-testid="workspace-error-boundary" para tests E2E existentes.
 */
class WorkspaceErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[WorkspaceErrorBoundary]', error, errorInfo);
    try {
      window.__damyxia_errors = window.__damyxia_errors || [];
      window.__damyxia_errors.push({
        type: 'react_boundary',
        message: String(error && error.message ? error.message : error || 'unknown'),
        timestamp: Date.now(),
        source: 'WorkspaceErrorBoundary',
      });
      // Cualquier consumidor puede escuchar este evento para mostrar el
      // panel opcional si desea.
      window.dispatchEvent(new CustomEvent('damyxia:silent-error', {
        detail: { source: 'WorkspaceErrorBoundary', error: String(error) },
      }));
      // 🟥 CUADRO NEGRO 2026-07-03 — Telemetría silenciosa al backend.
      reportSilentError({
        type: 'react_boundary',
        error: String(error && error.message ? error.message : error || 'unknown'),
        timestamp: Date.now(),
        component: 'WorkspaceErrorBoundary',
        extra: {
          componentStack: (errorInfo && errorInfo.componentStack) ? String(errorInfo.componentStack).slice(0, 3800) : undefined,
        },
      });
    } catch (_) { /* silent */ }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  componentDidMount() {
    // Auto-reset cuando el user pide "corregir y enviar" — cambia de
    // children reactivamente. Si el mismo boundary tiene nuevos children,
    // reseteamos silenciosamente. Handled via key change desde el padre.
    window.addEventListener('damyxia:workspace-retry', this.handleReset);
  }

  componentWillUnmount() {
    window.removeEventListener('damyxia:workspace-retry', this.handleReset);
  }

  render() {
    if (this.state.hasError) {
      // MOSTRAR SIEMPRE — contenedor vacío. Sin banners rojos, sin amber,
      // sin botón de retry visible. El chat sigue funcionando y puede
      // enviar el componente corregido, o el user puede disparar
      // damyxia:workspace-retry desde otro lugar.
      return (
        <div
          className="h-full w-full bg-transparent"
          data-testid="workspace-error-boundary"
          data-error-state="silent"
        />
      );
    }
    return this.props.children;
  }
}

export default WorkspaceErrorBoundary;
